import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { X, FolderOpen, Archive, Upload, Smartphone, Copy, FileText, ChevronRight, ChevronDown, Loader2 } from 'lucide-react';
import { archiveReady, archiveName, archiveConnect, archiveHasStored, readIndex, archiveProject, archiveBackup, archiveProjectFile, archiveReportFile } from '../lib/archive';
import { download } from '../lib/exporters';

const fill = (s, o) => Object.entries(o).reduce((a, [k, v]) => a.replace(`{${k}}`, v), s);
const has = (v, s) => !s || String(v || '').toLowerCase().includes(s.trim().toLowerCase());
const day = (s, end) => (s ? new Date(`${s}T${end ? '23:59:59' : '00:00:00'}`).getTime() : null);
// quick field (name / place / customer) + separate fields + period overlapping created … last change
export const matches = (e, f) => {
  let from = day(f.from), to = day(f.to, true);
  if (from != null && to != null && from > to) [from, to] = [day(f.to), day(f.from, true)];
  const a = e.createdAt || e.archivedAt, b = e.updatedAt || e.archivedAt;
  return (has(e.title, f.q) || has(e.place, f.q) || has(e.customer, f.q)) && has(e.title, f.name) && has(e.place, f.place) && has(e.customer, f.customer)
    && (from == null || (b || 0) >= from) && (to == null || (a || 0) <= to);
};
const groupTree = (ix, f) => {
  const tree = {};
  Object.entries(ix.projects || {}).filter(([, e]) => matches(e, f)).forEach(([id, e]) => {
    const [b, a] = e.path; ((tree[b] ||= {})[a] ||= []).push({ id, ...e });
  });
  Object.values(tree).forEach((as) => Object.values(as).forEach((l) => l.sort((x, y) => (y.archivedAt || 0) - (x.archivedAt || 0))));
  return tree;
};
const openPdf = (f) => window.open(URL.createObjectURL(f), '_blank', 'noopener');
const NOF = { q: '', name: '', place: '', customer: '', from: '', to: '' };

function Search({ t, f, setF, ix }) {
  const [adv, setAdv] = useState(false);
  const all = Object.values(ix.projects || {}), n = all.filter((e) => matches(e, f)).length, active = Object.values(f).some(Boolean);
  const inp = (k, ph, type = 'text') => <input className="input" type={type} placeholder={ph} title={ph} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`archive-filter-${k}`} />;
  return (
    <div className="ar-search" data-testid="archive-search">
      <div className="ar-srow">
        <input className="input" placeholder={t.arSearch} value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} data-testid="archive-search-input" />
        <label className="ar-date">{t.arFrom} {inp('from', t.arFrom, 'date')}</label>
        <label className="ar-date">{t.arTo} {inp('to', t.arTo, 'date')}</label>
        <button className="btn btn-outline btn-sm" data-testid="archive-adv-toggle" onClick={() => setAdv(!adv)}>{t.arAdv}</button>
        {active && <button className="btn btn-outline btn-sm" data-testid="archive-filter-clear" onClick={() => setF(NOF)}>{t.arClear}</button>}
      </div>
      {adv && <div className="ar-srow" data-testid="archive-adv">{inp('name', t.arName)}{inp('place', t.arPlace)}{inp('customer', t.arCustomer)}</div>}
      {active && <small className="muted" data-testid="archive-hits">{t.arHits.replace('{n}', n)}{all.some((e) => e.place === undefined) ? ` · ${t.arOldHint}` : ''}</small>}
    </div>
  );
}
const repCount = (e) => Object.keys(e.reports || {}).length + (e.extra || []).length;
const latest = (list) => list.filter((e) => e.phoneAt).sort((a, b) => b.phoneAt - a.phoneAt)[0];

// per branch: projects, reports, last arrival from the phone
function Overview({ t, ix }) {
  const all = Object.values(ix.projects || {});
  if (!all.length) return null;
  const by = {};
  all.forEach((e) => (by[e.path[0]] ||= []).push(e));
  const last = latest(all), when = (v) => new Date(v).toLocaleString(t.locale);
  return (
    <div className="ar-ov" data-testid="archive-overview">
      <p className="ar-last" data-testid="archive-last-phone">📱 {t.arLastPhone}: {last ? <b>{last.title} · {last.path[0]} · {when(last.phoneAt)}</b> : <span className="muted">{t.arNoPhone}</span>}</p>
      <div className="ar-cards">
        {Object.keys(by).sort().map((b) => {
          const l = latest(by[b]);
          return (
            <div key={b} className="ar-card" data-testid={`archive-stat-${b}`}>
              <b>📁 {b}</b>
              <span data-testid={`archive-stat-projects-${b}`}>{by[b].length} {t.arProjects}</span>
              <span data-testid={`archive-stat-reports-${b}`}>{by[b].reduce((s, e) => s + repCount(e), 0)} {t.arReports}</span>
              <small className="muted" data-testid={`archive-stat-phone-${b}`}>📱 {l ? `${l.title} · ${when(l.phoneAt)}` : t.arNoPhone}</small>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Connect({ t, onDone, ready }) {
  const [stored, setStored] = useState(false);
  useEffect(() => { archiveHasStored().then(setStored); }, []);
  const go = (n) => archiveConnect(n).then(onDone, (e) => e?.name !== 'AbortError' && toast.error(t.arErr + (e?.message || e)));
  return (
    <div className="row ar-connect">
      {stored && !ready && <button className="btn btn-play" data-testid="archive-reconnect-btn" onClick={() => go(false)}><FolderOpen size={16} /> {t.arReconnect}</button>}
      <button className={`btn ${stored || ready ? 'btn-outline' : 'btn-play'}`} data-testid="archive-connect-btn" onClick={() => go(true)}><FolderOpen size={16} /> {stored || ready ? t.arChange : t.arConnect}</button>
    </div>
  );
}

function ProjectRow({ t, p, project, onOpen, busy }) {
  const [open, setOpen] = useState(false);
  const toPhone = async () => {
    const f = await archiveProjectFile(p.id).catch(() => null);
    if (!f) return toast.error(t.arNoFile);
    const file = new File([f], `${(p.title || 'projekt').replace(/[^\w-]+/g, '_').slice(0, 40)}.giss.json`, { type: 'application/json' });
    if (navigator.canShare?.({ files: [file] })) await navigator.share({ files: [file], title: file.name }).catch(() => {});
    else download(file, file.name);
    toast.message(t.arPhoneHint, { duration: 9000 });
  };
  const backup = async () => { if (await archiveBackup(project)) toast.success(t.arBackupOk); };
  const reps = Object.values(p.reports || {});
  return (
    <li className="ar-proj" data-testid={`archive-project-${p.id}`}>
      <div className="ar-row">
        <button className="ar-tog" data-testid={`archive-project-toggle-${p.id}`} onClick={() => setOpen(!open)}>{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />} <b>{p.title}</b></button>
        <small className="muted">{reps.length} {t.arReports} · {t.arLast} {new Date(p.archivedAt).toLocaleString(t.locale)}</small>
        <button className="btn btn-outline btn-sm" disabled={busy} data-testid={`archive-open-${p.id}`} onClick={() => onOpen(p.id)}>{t.arOpen}</button>
        <button className="btn btn-outline btn-sm" data-testid={`archive-phone-${p.id}`} onClick={toPhone}><Smartphone size={14} /> {t.arToPhone}</button>
        {project?.id === p.id && <button className="btn btn-outline btn-sm" data-testid={`archive-backup-${p.id}`} onClick={backup}><Copy size={14} /> {t.arBackup}</button>}
      </div>
      {open && (
        <ul className="ar-reps">
          {[...reps.map((r) => [r.file, r.title]), ...(p.extra || []).map((f) => [f, f])].map(([file, title]) => (
            <li key={file}><button className="ar-pdf" data-testid={`archive-pdf-${file}`} onClick={() => archiveReportFile(p.id, file).then(openPdf, () => toast.error(t.arNoFile))}><FileText size={13} /> {title}</button></li>
          ))}
        </ul>
      )}
    </li>
  );
}

function Tree({ t, ix, f, project, onOpen, busy }) {
  const tree = groupTree(ix, f), branches = Object.keys(tree).sort();
  if (!branches.length) return <p className="muted" data-testid="archive-empty">{t.arEmpty}</p>;
  return (
    <div className="ar-tree" data-testid="archive-tree">
      {branches.map((b) => (
        <details key={b} open className="ar-branch" data-testid={`archive-branch-${b}`}>
          <summary>📁 {b}</summary>
          {Object.keys(tree[b]).sort().map((a) => (
            <details key={a} open className="ar-area" data-testid={`archive-area-${b}-${a}`}>
              <summary>📂 {a}</summary>
              <ul>{tree[b][a].map((p) => <ProjectRow key={p.id} t={t} p={p} project={project} onOpen={onOpen} busy={busy} />)}</ul>
            </details>
          ))}
        </details>
      ))}
    </div>
  );
}

// desktop-only project archive: branch / area / project / reports in a real folder
export default function ArchiveDialog({ t, lang, project, projects, onImport, onClose }) {
  const [ready, setReady] = useState(archiveReady());
  const [ix, setIx] = useState({ projects: {} });
  const [f, setF] = useState(NOF);
  const [busy, setBusy] = useState('');
  const reload = () => readIndex().then(setIx, () => {});
  useEffect(() => { const on = () => { setReady(archiveReady()); if (archiveReady()) reload(); }; on(); window.addEventListener('giss-archive', on); return () => window.removeEventListener('giss-archive', on); }, []);
  const run = async (list) => {
    try {
      for (let k = 0; k < list.length; k++) await archiveProject(list[k], lang, (i, n) => setBusy(list.length > 1 ? `${list[k].title} · ${fill(t.arBusy, { i: k + 1, n: list.length })}` : fill(t.arBusy, { i, n })));
      toast.success(fill(t.arSaved, { p: list.length > 1 ? list.length : list[0].title }));
    } catch (e) { toast.error(t.arErr + (e?.message || e)); }
    setBusy('');
  };
  const openArchived = async (id) => { const f = await archiveProjectFile(id).catch(() => null); if (!f) return toast.error(t.arNoFile); if (await onImport(f, false, true)) onClose(); };
  return createPortal(
    <div className="editor-backdrop" data-testid="archive-dialog" onClick={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="editor ar-dlg">
        <div className="editor-head">
          <h4><Archive size={17} /> {t.arTitle}</h4>
          <button className="icon-btn" data-testid="archive-close-btn" onClick={onClose}><X size={18} /></button>
        </div>
        <p className="muted small-hint">{t.arIntro}</p>
        {!ready ? <Connect t={t} onDone={reload} /> : (
          <>
            <div className="row ar-head">
              <span className="ar-folder" data-testid="archive-folder-name">📁 {archiveName()}</span>
              <Connect t={t} onDone={reload} ready />
            </div>
            <div className="row">
              <button className="btn btn-play" disabled={!!busy || !project} data-testid="archive-save-current-btn" onClick={() => run([project])}><Archive size={16} /> {t.arSaveCur}</button>
              <button className="btn btn-outline" disabled={!!busy} data-testid="archive-save-all-btn" onClick={() => run(projects.filter((p) => !p.__fresh))}>{t.arSaveAll}</button>
              <label className="btn btn-outline" data-testid="archive-import-label"><Upload size={16} /> {t.arImport}
                <input type="file" hidden accept=".json,.giss,application/json" data-testid="archive-import-input" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onImport(f); }} />
              </label>
            </div>
            {busy && <p className="ar-busy" data-testid="archive-busy"><Loader2 size={14} className="spin" /> {busy}</p>}
            <Overview t={t} ix={ix} />
            <Search t={t} f={f} setF={setF} ix={ix} />
            <Tree t={t} ix={ix} f={f} project={project} onOpen={openArchived} busy={!!busy} />
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
